import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "include", "academicYear", "matchSelect", "priorSeasonCell", "matchTypeCell", "submitButton", "notReturningRow", "errorMessages"]

  connect() {
    console.log("Hello from the Roster Import Review Controller!");

    this.validateForm()
    this.syncNotReturningRows()
    this.disableAlreadySelectedMatchOptions()
  }

  toggleRow() {
    this.validateForm()
    this.syncNotReturningRows()
    this.disableAlreadySelectedMatchOptions()
  }

  matchChanged(event) {
    const row = event.target.closest("tr")
    const matchTypeCell = row.querySelector("[data-roster-import-review-target='matchTypeCell']")
    const priorSeasonCell = row.querySelector("[data-roster-import-review-target='priorSeasonCell']")

    if (event.target.value === "") {
      matchTypeCell.innerHTML = '<span class="fw-pill font-fanword-bold fw-pill--neutral">New player</span>'
      priorSeasonCell.textContent = "—"
    } else {
      matchTypeCell.innerHTML = '<span class="fw-pill font-fanword-bold fw-pill--pending">Manual match</span>'
    }

    this.validateForm()
    this.syncNotReturningRows()
    this.disableAlreadySelectedMatchOptions()
  }

  validateForm() {
    let allIncludedRowsHaveAcademicYear = true
    let hasDuplicateExistingPlayerMatches = false
    const matchedPlayerIdsToCount = {}

    this.rowTargets.forEach((row) => {
      const includeCheckbox = row.querySelector("[data-roster-import-review-target='include']")
      const academicYearSelect = row.querySelector("[data-roster-import-review-target='academicYear']")
      const matchSelect = row.querySelector("[data-roster-import-review-target='matchSelect']")
      const isIncluded = includeCheckbox && includeCheckbox.checked

      if (isIncluded && academicYearSelect && academicYearSelect.value === "") {
        allIncludedRowsHaveAcademicYear = false
        academicYearSelect.classList.add('is-invalid')
      } else if (academicYearSelect) {
        academicYearSelect.classList.remove('is-invalid')
      }

      if (isIncluded && matchSelect && matchSelect.value !== "") {
        const playerId = matchSelect.value
        matchedPlayerIdsToCount[playerId] = (matchedPlayerIdsToCount[playerId] || 0) + 1
      }
    })

    this.rowTargets.forEach((row) => {
      const includeCheckbox = row.querySelector("[data-roster-import-review-target='include']")
      const matchSelect = row.querySelector("[data-roster-import-review-target='matchSelect']")
      if (!matchSelect) return

      const isIncluded = includeCheckbox && includeCheckbox.checked
      const playerId = matchSelect.value
      const isDuplicateMatch = isIncluded && playerId !== "" && matchedPlayerIdsToCount[playerId] > 1

      if (isDuplicateMatch) {
        hasDuplicateExistingPlayerMatches = true
        matchSelect.classList.add('is-invalid')
      } else {
        matchSelect.classList.remove('is-invalid')
      }
    })

    const formIsValid = allIncludedRowsHaveAcademicYear && !hasDuplicateExistingPlayerMatches
    const errorMessages = []
    if (!allIncludedRowsHaveAcademicYear) {
      errorMessages.push("All included rows must have academic year selected.")
    }
    if (hasDuplicateExistingPlayerMatches) {
      errorMessages.push("Each existing player can only be matched to one incoming player.")
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !formIsValid
    }

    if (this.hasErrorMessagesTarget) {
      this.errorMessagesTarget.innerHTML = errorMessages.join(" ")
    }
  }

  disableAlreadySelectedMatchOptions() {
    if (!this.hasMatchSelectTarget) return

    const selectedPlayerIds = []
    this.matchSelectTargets.forEach((matchSelect) => {
      const row = matchSelect.closest("tr")
      const includeCheckbox = row.querySelector("[data-roster-import-review-target='include']")
      const isIncluded = includeCheckbox && includeCheckbox.checked
      if (isIncluded && matchSelect.value !== "") {
        selectedPlayerIds.push(matchSelect.value)
      }
    })

    this.matchSelectTargets.forEach((matchSelect) => {
      const currentValue = matchSelect.value
      Array.from(matchSelect.options).forEach((option) => {
        if (option.value === "") {
          option.disabled = false
          return
        }

        const optionIsSelectedOnThisRow = option.value === currentValue
        const optionIsSelectedOnAnotherRow = selectedPlayerIds.includes(option.value) && !optionIsSelectedOnThisRow
        option.disabled = optionIsSelectedOnAnotherRow
      })
    })
  }

  syncNotReturningRows() {
    if (!this.hasNotReturningRowTarget) return

    const matchedPlayerIds = this.rowTargets.flatMap((row) => {
      const includeCheckbox = row.querySelector("[data-roster-import-review-target='include']")
      if (includeCheckbox && !includeCheckbox.checked) return []

      const matchSelect = row.querySelector("[data-roster-import-review-target='matchSelect']")
      if (matchSelect && matchSelect.value !== "") return [matchSelect.value]
      return []
    })

    this.notReturningRowTargets.forEach((row) => {
      const playerId = row.dataset.playerId
      const isMatched = matchedPlayerIds.includes(playerId)

      row.classList.toggle("opacity-50", isMatched)

      const matchedNotice = row.querySelector("[data-roster-import-review-target='notReturningMatchedNotice']")
      if (matchedNotice) matchedNotice.classList.toggle("d-none", !isMatched)

      row.querySelectorAll("input, select").forEach((input) => {
        input.disabled = isMatched

        if (input.type === "checkbox") {
          input.checked = isMatched ? false : true
        }

        if (isMatched && input.tagName === "SELECT") {
          input.value = ""
        }
      })
    })
  }

  resetAll(event) {
    if (event.currentTarget.dataset.confirmSwal) {
      // need to confirm first
      return;
    }

    console.log("Resetting all rows...", event.params.column);
    const columnParam = event.params.column;
    if (!columnParam) return

    // For each row, find ALL input/select elements whose id ends with the columnParam value and clear their value
    this.rowTargets.forEach((row) => {
      const inputs = row.querySelectorAll(`[id$='${columnParam}']`);
      inputs.forEach((input) => {
        input.value = "";
      });
    });
  }
}
